import { useEffect, useState } from "react";

import { createFromTemplate, getTemplates, type ProgramTemplate } from "../lib/api";
import {
  complete,
  dismiss,
  questionsFor,
  suggestTemplate,
  type Answers,
  type Experience,
  type Frequency,
  type Where,
} from "../lib/onboarding";
import { haptic, notify, openTelegramLink } from "../lib/telegram";
import PixelField from "./PixelField";
import TemplateCard from "./TemplateCard";
import { BusyLabel } from "./DotLoader";

const BOT_URL = "https://t.me/rackgymbot";

interface Option<T> {
  value: T;
  title: string;
  hint: string;
}

const WHERE: Option<Where>[] = [
  { value: "gym", title: "В зале", hint: "Штанга, тренажёры, гантели" },
  { value: "home", title: "Дома с гантелями", hint: "Пара гантелей и пол" },
  { value: "bodyweight", title: "Без железа", hint: "Свой вес и турник" },
];

const EXPERIENCE: Option<Experience>[] = [
  { value: "new", title: "Только начинаю", hint: "Или вернулся после долгого перерыва" },
  { value: "trained", title: "Тренируюсь давно", hint: "Знаю присед, жим и тягу" },
];

const FREQUENCY: Option<Frequency>[] = [
  { value: 3, title: "3 раза в неделю", hint: "Всё тело за тренировку" },
  { value: 4, title: "4 раза", hint: "Верх и низ по отдельности" },
  { value: 5, title: "5 и больше", hint: "Сплит по группам мышц" },
];

const QUESTIONS = {
  where: { title: "Где тренируешься?", options: WHERE },
  experience: { title: "Какой опыт?", options: EXPERIENCE },
  frequency: { title: "Сколько раз в неделю?", options: FREQUENCY },
} as const;

/**
 * Знакомство при первом входе (21-retention-plan.md §6).
 *
 * До трёх вопросов — и готовая программа уже в календаре с сегодняшнего дня.
 * Раньше новый человек видел «Программы пока нет» и уходил: из 15 человек
 * хотя бы один подход записали двое (20-review.md).
 */
export default function Onboarding({
  onStarted,
  onBrowse,
  onSkip,
}: {
  onStarted: () => void;
  onBrowse: () => void;
  onSkip: () => void;
}) {
  const [answers, setAnswers] = useState<Partial<Answers>>({});
  const [templates, setTemplates] = useState<ProgramTemplate[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, []);

  const asked = questionsFor(answers);
  const pending = asked.find((q) => answers[q] === undefined);
  const done = complete(answers);
  const suggested = done ? templates.find((t) => t.key === suggestTemplate(answers)) : undefined;

  function answer(question: keyof Answers, value: Answers[keyof Answers]) {
    haptic();
    setAnswers((prev) => {
      const next = { ...prev, [question]: value };
      // Сменили «где» — ответы про зал больше не про то.
      if (question === "where") return { where: value as Where };
      return next;
    });
  }

  async function start() {
    if (!suggested || busy) return;
    haptic("medium");
    setBusy(true);
    try {
      await createFromTemplate(suggested.key, true);
      notify("success");
      onStarted();
    } catch {
      notify("error");
      setBusy(false);
    }
  }

  function skip() {
    haptic();
    dismiss();
    onSkip();
  }

  // Без «из N»: число вопросов зависит от ответов (дома — один, в зале — до
  // трёх), и «1 из 2», ставшее «2 из 4», читалось бы как сбой.
  const step = done ? asked.length + 1 : asked.indexOf(pending!) + 1;

  return (
    <div className="onb">
      <div className="onb__head">
        <PixelField className="onb__field" seed={3} />
        <span className="eyebrow">{done ? "Готово" : `Шаг ${step}`}</span>
        <h1 className="onb__title">
          {done ? "Программа готова" : QUESTIONS[pending!].title}
        </h1>
        {step === 1 && (
          <p className="lead" style={{ margin: 0 }}>
            Подберём программу и сразу поставим её в календарь — первая тренировка
            сегодня.
          </p>
        )}
      </div>

      {!done && (
        <div className="stack">
          {QUESTIONS[pending!].options.map((option) => (
            <button
              key={String(option.value)}
              className="onb__opt"
              onClick={() => answer(pending!, option.value as never)}
            >
              <span className="onb__opt-title">{option.title}</span>
              <span className="onb__opt-hint">{option.hint}</span>
            </button>
          ))}
          {step > 1 && (
            <button
              className="link-button"
              onClick={() => {
                haptic();
                setAnswers((prev) => {
                  const next = { ...prev };
                  delete next[asked[step - 2]];
                  return next;
                });
              }}
            >
              ← Назад
            </button>
          )}
        </div>
      )}

      {done && (
        <div className="stack">
          {suggested ? (
            <TemplateCard template={suggested}>
              <button className="button button--wide" disabled={busy} onClick={() => void start()}>
                {busy ? <BusyLabel text="Собираю" /> : "Начать сегодня"}
              </button>
            </TemplateCard>
          ) : (
            <p className="lead" style={{ margin: 0 }}>Загрузка…</p>
          )}
          <button className="link-button" onClick={() => { haptic(); onBrowse(); }}>
            Показать все программы
          </button>
          <button
            className="link-button"
            onClick={() => {
              haptic();
              setAnswers({});
            }}
          >
            Ответить заново
          </button>
        </div>
      )}

      <div className="onb__foot">
        {/* Своя таблица — исходный путь приложения: бот разбирает файл сам. */}
        <button
          className="link-button"
          onClick={() => {
            haptic();
            dismiss();
            openTelegramLink(BOT_URL);
          }}
        >
          У меня своя таблица — пришлю боту
        </button>
        <button className="link-button onb__skip" onClick={skip}>
          Пропустить
        </button>
      </div>
    </div>
  );
}
